import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { HttpError } from '../../api/client';
import type {
  MatchingBoardItemDto,
  MatchingPreferenceDto,
  MatchingRequestCommand,
  MatchingWindowDto,
} from '../../api/matchingTypes';
import {
  clubToday,
  formatClubTime,
  formatClubWindow,
  formatDateKeyRu,
  hasErrors,
  isClubMember,
  isDateSelectable,
  matchingErrorMessage,
  selectableDates,
  validateRequestForm,
} from '../../domain/matching/matching';
import { PreferencePicker } from '../../features/matching/PreferencePicker';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectMatchingBoard, selectMatchingMeta } from '../../store/selectors/matchingSelectors';
import { uiActions } from '../../store/slices/uiSlice';
import {
  createMatchingRequest,
  fetchMatchingBoard,
  fetchMatchingMeta,
  previewMatchingRequest,
  updateMatchingRequest,
} from '../../store/thunks';
import { TavernEmptyState, TavernField, TavernLoading } from '../../ui/components';

import './MatchingRequestPage.css';

const PREVIEW_DEBOUNCE_MS = 300;

function errorInfo(err: unknown, fallback: string): { code?: string; message: string } {
  const code = err instanceof HttpError ? err.code : undefined;
  const message = err instanceof Error ? err.message : fallback;
  return { code, message: matchingErrorMessage(code, message) };
}

/** Create (/gathering/new) or edit (/gathering/:id/edit) a search request. */
export function MatchingRequestPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = id ? Number(id) : null;
  const isEdit = requestId != null;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const role = useAppSelector((s) => s.auth.user?.role);
  const meta = useAppSelector(selectMatchingMeta);
  const board = useAppSelector(selectMatchingBoard);
  const knownVersion = useAppSelector((s) =>
    requestId != null ? s.matching.requestVersions[String(requestId)] : undefined,
  );

  const today = meta?.today ?? clubToday();
  const dates = React.useMemo(() => selectableDates(today), [today]);

  // The request being edited: router state first, then the loaded board.
  const editItem: MatchingBoardItemDto | undefined = React.useMemo(() => {
    if (requestId == null) return undefined;
    const fromState = (location.state as { item?: MatchingBoardItemDto } | null)?.item;
    return fromState ?? board.items.find((i) => i.id === requestId);
  }, [location.state, board.items, requestId]);

  const [date, setDate] = React.useState<string>(() => {
    const fromQuery = searchParams.get('date');
    return fromQuery && isDateSelectable(fromQuery, clubToday()) ? fromQuery : clubToday();
  });
  const [startFrom, setStartFrom] = React.useState('18:00');
  const [startTo, setStartTo] = React.useState('20:00');
  const [preferences, setPreferences] = React.useState<MatchingPreferenceDto[]>([]);
  const [initialized, setInitialized] = React.useState(!isEdit);

  const [preview, setPreview] = React.useState<MatchingWindowDto | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!meta) void dispatch(fetchMatchingMeta());
  }, [dispatch, meta]);

  // Direct navigation to the edit URL: load the board to find the request.
  React.useEffect(() => {
    if (isEdit && !editItem && board.items.length === 0 && !board.isLoading) {
      void dispatch(fetchMatchingBoard({ page: 0 }));
    }
  }, [dispatch, isEdit, editItem, board.items.length, board.isLoading]);

  React.useEffect(() => {
    if (!isEdit || initialized || !editItem) return;
    setDate(editItem.gameDate);
    setStartFrom(formatClubTime(editItem.windowStart));
    setStartTo(formatClubTime(editItem.windowEnd));
    setPreferences(editItem.preferences);
    setInitialized(true);
  }, [isEdit, initialized, editItem]);

  const errors = validateRequestForm(
    { date, startFrom, startTo, preferences },
    today,
    meta?.limits,
  );
  const formValid = !hasErrors(errors);

  const buildCommand = React.useCallback(
    (expectedWindowStart?: string | null): MatchingRequestCommand => ({
      gameDate: date,
      startFrom,
      startTo,
      preferences,
      expectedWindowStart: expectedWindowStart ?? null,
    }),
    [date, startFrom, startTo, preferences],
  );

  // Show the normalized window before the request is stored (debounced, stale responses ignored).
  React.useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!formValid || !initialized || !isClubMember(role)) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      dispatch(previewMatchingRequest(buildCommand()))
        .unwrap()
        .then((res) => {
          if (!cancelled) setPreview(res);
        })
        .catch((err: unknown) => {
          if (!cancelled) setPreviewError(errorInfo(err, 'Не удалось проверить окно').message);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dispatch, buildCommand, formValid, initialized, role]);

  const version = editItem?.version ?? knownVersion;
  const versionMissing = isEdit && initialized && version == null;
  const canSubmit = formValid && !!preview && !isSubmitting && !versionMissing;

  const goBack = () => navigate('/gathering');

  const onSubmit = async () => {
    if (!preview) return;
    setIsSubmitting(true);
    try {
      const command = buildCommand(preview.windowStart);
      if (isEdit && requestId != null && version != null) {
        await dispatch(updateMatchingRequest({ id: requestId, version, command })).unwrap();
      } else {
        await dispatch(createMatchingRequest(command)).unwrap();
      }
      dispatch(uiActions.setNotification({ type: 'success', message: 'Заявка сохранена' }));
      navigate('/gathering');
    } catch (err) {
      const info = errorInfo(err, 'Не удалось сохранить заявку');
      dispatch(uiActions.setNotification({ type: 'error', message: info.message }));
      // A stale preview or a concurrent change: re-check the window.
      if (info.code === 'MATCHING_PREVIEW_EXPIRED') setPreview(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isClubMember(role)) {
    return (
      <div className="ttbPage matching-request-page">
        <TavernEmptyState icon="🔒" title="Сбор игроков" description="Доступно членам клуба" />
      </div>
    );
  }

  if (isEdit && !initialized) {
    return (
      <div className="ttbPage matching-request-page">
        {board.isLoading ? (
          <TavernLoading message="Загрузка…" />
        ) : (
          <TavernEmptyState
            icon="🗺️"
            title="Заявка не найдена"
            description="Возможно, она уже закрыта или снята с поиска"
            action={
              <button type="button" className="ttbButton" onClick={goBack}>
                К сбору
              </button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="ttbPage matching-request-page">
      <div className="ttbFormContainer">
        <div className="ttbFormHeader">
          <button
            type="button"
            onClick={goBack}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            ← Назад
          </button>
          <div className="ttbFormHeaderTitle">{isEdit ? 'Изменить заявку' : 'Новая заявка'}</div>
          <button
            type="button"
            className="ttbButton ttbButtonOutlineHeader ttbFormHeaderSpacer"
            disabled
            aria-hidden="true"
            tabIndex={-1}
          >
            ← Назад
          </button>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">📅</span>
            <h2 className="ttbFormSectionTitle">Когда</h2>
          </div>

          <TavernField label="Дата" required error={errors.date}>
            <select
              className="ttbInput"
              value={date}
              disabled={isSubmitting}
              onChange={(e) => setDate(e.target.value)}
            >
              {dates.map((d) => (
                <option key={d} value={d}>
                  {formatDateKeyRu(d)}
                  {d === today ? ' (сегодня)' : ''}
                </option>
              ))}
            </select>
          </TavernField>

          <TavernField label="Могу начать с" required>
            <input
              type="time"
              step={300}
              className="ttbInput"
              value={startFrom}
              disabled={isSubmitting}
              onChange={(e) => setStartFrom(e.target.value)}
            />
          </TavernField>

          <TavernField label="Могу начать до" required error={errors.time}>
            <input
              type="time"
              step={300}
              className="ttbInput"
              value={startTo}
              disabled={isSubmitting}
              onChange={(e) => setStartTo(e.target.value)}
            />
          </TavernField>

          {preview ? (
            <div className="matching-window-preview">
              Окно начала после проверки: {formatClubWindow(preview.windowStart, preview.windowEnd)}
            </div>
          ) : previewError ? (
            <div className="matching-window-preview matching-window-preview--error">{previewError}</div>
          ) : formValid ? (
            <div className="matching-window-preview">Проверяем окно…</div>
          ) : null}
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">🎲</span>
            <h2 className="ttbFormSectionTitle">Предпочтения</h2>
          </div>
          {meta ? (
            <PreferencePicker
              meta={meta}
              value={preferences}
              disabled={isSubmitting}
              onChange={setPreferences}
            />
          ) : (
            <TavernLoading message="Загрузка игр…" />
          )}
          {errors.preferences ? <div className="ttbFieldError">{errors.preferences}</div> : null}
          <div className="ttbFieldHint">
            Без предпочтений подойдёт любая игра. Игру за столом выбирают сами игроки: выбранные игры и теги попадут в
            описание события. «Хочу» помогает собрать единомышленников, а «Не хочу» не посадит вас за один стол с теми,
            кто этого хочет.
          </div>
        </div>

        {versionMissing ? (
          <div className="ttbFieldError">
            Не удалось определить версию заявки. Создайте новую заявку или обновите страницу.
          </div>
        ) : null}

        <div className="ttbFormActions">
          <button
            type="button"
            onClick={goBack}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => {
              void onSubmit();
            }}
            disabled={!canSubmit}
            className="ttbButton ttbButtonSubmit"
          >
            {isSubmitting ? 'Сохранение…' : isEdit ? '✨ Сохранить' : '✨ Оставить заявку'}
          </button>
        </div>
      </div>
    </div>
  );
}
